import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import DataPill from "@components/pills/DataPill";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { kycService } from "@services/apis/Kyc";
import { FetchKycDataForApprovalData } from "@services/models/Kyc/FetchKycDataForApprovalModel";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isPaysonicHost } from "src/config/deploymentConfig";
import SettledAccountReponseSlider from "../../modules/settleaccounts/components/SettledAccountReponseSlider";
import ConstantFunctions from "../../utils/constants_functions";
import KycApprovalDetails from "./KycApprovalDetails";

const KycApprovalList: React.FC = () => {
  const constantFunctions = new ConstantFunctions();
  const isPaysonic = isPaysonicHost();
  const gridRef = useRef<AgGridReact>(null);
  const loadingRef = useRef<boolean>(false);
  const [kycData, setKycData] = useState<FetchKycDataForApprovalData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const rowsPerPage = 1000;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [filterKycStatus, setFilterKycStatus] = useState<string | undefined>();

  const [showDumpModal, setShowDumpModal] = useState<boolean>(false);
  const [modalData, setModalData] = useState<any>(null);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [selectedKycData, setSelectedKycData] =
    useState<FetchKycDataForApprovalData | null>(null);

  const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Created At",
        field: "createdAt",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 140,
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Name",
        field: "fullName",
        flex: 1.2,
        minWidth: 130,
        maxWidth: 200,
        valueGetter: (params) =>
          `${params.data?.firstName ?? ""} ${params.data?.lastName ?? ""}`.trim() ||
          "-",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "User ID",
        field: "userId",
        flex: 0.7,
        minWidth: 80,
        maxWidth: 120,
        cellRenderer: (params: any) => {
          const { data } = params;
          const userId = data?.userId;
          if (userId == null) {
            return (
              <span className="text-[10px] text-text-base font-medium">-</span>
            );
          }
          return (
            <div className="flex items-center justify-between h-full gap-1">
              <span className="text-[10px] text-text-base font-medium truncate">
                {userId}
              </span>
              <CopyButton
                copyText={userId.toString()}
                copyMessage="User ID copied!"
                copyIconSize={10}
                buttonSize="14px"
                borderRadius="2px"
              />
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Email",
        field: "email",
        flex: 2,
        minWidth: 200,
        maxWidth: 300,
        cellRenderer: (params: any) => {
          const { data } = params;
          const email = data?.email;
          if (!email) {
            return (
              <span className="text-[10px] text-text-base font-medium">-</span>
            );
          }
          return (
            <div className="flex items-center justify-between h-full gap-1">
              <span className="text-[10px] text-text-base font-medium truncate flex-1">
                {email}
              </span>
              <CopyButton
                copyText={email}
                copyMessage="Email copied!"
                copyIconSize={10}
                buttonSize="14px"
                borderRadius="2px"
              />
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "ID Status",
        field: "poiStatus",
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: ({ data }: any) => <DataPill value={data?.poiStatus} />,
      },
      {
        headerName: "Address Status",
        field: "poaStatus",
        flex: 1.1,
        minWidth: 110,
        maxWidth: 160,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: ({ data }: any) => <DataPill value={data?.poaStatus} />,
      },
      {
        headerName: "Overall Status",
        field: "overallKycStatus",
        flex: 1.1,
        minWidth: 110,
        maxWidth: 160,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: ({ data }: any) => (
          <DataPill value={data?.overallKycStatus} />
        ),
      },
      {
        headerName: "ID Dump",
        field: "idDump",
        flex: 1,
        minWidth: 110,
        maxWidth: 160,
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <LightCustomButton
              text="View ID Data"
              isActive={true}
              onClick={() =>
                handleShowDump(data.poiDump, "ID Verification Data")
              }
              textSize="10px"
              className="!h-[24px] !flex-none w-full flex items-center justify-center"
            />
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: false,
        sortable: false,
      },
      {
        headerName: "Address Dump",
        field: "addressDump",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 180,
        cellRenderer: (props: any) => {
          const { data } = props;
          return (
            <LightCustomButton
              text="View Address Data"
              isActive={true}
              onClick={() =>
                handleShowDump(data.poaDump, "Address Verification Data")
              }
              textSize="10px"
              className="!h-[24px] !flex-none w-full flex items-center justify-center"
            />
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: false,
        sortable: false,
      },
    ],
    [constantFunctions],
  );

  const fetchKycApprovalData = useCallback(
    async (pageNumber = 1, append = false, search?: string) => {
      if (loadingRef.current) return;

      try {
        loadingRef.current = true;
        setLoading(true);
        const response = await kycService.fetchKYCRecords({
          page: pageNumber,
          limit: rowsPerPage,
          search: search,
          overallKycStatus: filterKycStatus?.toLowerCase(),
          accountType: isPaysonic ? "cashier" : undefined,
        });

        if (append) {
          setKycData((prev) => [...prev, ...response.data]);
        } else {
          setKycData(response.data);
        }

        setHasMore(response.data.length === rowsPerPage);
      } catch (err: any) {
        setError("Failed to fetch KYC approval data");
        console.error(err);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [filterKycStatus],
  );

  const resetListState = useCallback(() => {
    setKycData([]);
    setPage(1);
    setHasMore(true);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = useCallback(() => {
    const normalizedSearchTerm = searchTerm.trim();

    if (normalizedSearchTerm === appliedSearchTerm) {
      resetListState();
      fetchKycApprovalData(1, false, normalizedSearchTerm || undefined);
      return;
    }

    setAppliedSearchTerm(normalizedSearchTerm);
  }, [appliedSearchTerm, fetchKycApprovalData, resetListState, searchTerm]);

  const handleClearSearch = useCallback(() => {
    if (!searchTerm && !appliedSearchTerm) {
      return;
    }

    setSearchTerm("");

    if (appliedSearchTerm === "") {
      resetListState();
      fetchKycApprovalData(1, false);
      return;
    }

    setAppliedSearchTerm("");
  }, [appliedSearchTerm, fetchKycApprovalData, resetListState, searchTerm]);

  const handleKycStatusChange = useCallback((status: string) => {
    setFilterKycStatus((prev) => (prev === status ? undefined : status));
    setSearchTerm("");
    setAppliedSearchTerm("");
  }, []);

  const handleRefresh = useCallback(() => {
    resetListState();
    fetchKycApprovalData(1, false, appliedSearchTerm || undefined);
  }, [appliedSearchTerm, fetchKycApprovalData, resetListState]);

  const loadMoreData = useCallback(() => {
    if (!loadingRef.current && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchKycApprovalData(nextPage, true, appliedSearchTerm || undefined);
    }
  }, [appliedSearchTerm, fetchKycApprovalData, hasMore, page]);

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || loading) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        loadMoreData();
      }
    },
    [hasMore, loadMoreData, loading],
  );

  useEffect(() => {
    resetListState();
    fetchKycApprovalData(1, false, appliedSearchTerm || undefined);
  }, [
    appliedSearchTerm,
    fetchKycApprovalData,
    filterKycStatus,
    resetListState,
  ]);

  const handleShowDump = (dumpData: any, title: string) => {
    setModalData(dumpData);
    setModalTitle(title);
    setShowDumpModal(true);
  };

  const handleCloseDumpModal = () => {
    setShowDumpModal(false);
  };

  const handleRowClick = (item: FetchKycDataForApprovalData) => {
    setSelectedKycData(item);
  };

  const onRowClicked = (event: RowClickedEvent) => {
    const selectedData = event.data as FetchKycDataForApprovalData;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    handleRowClick(selectedData);
    setIsPanelCollapsed(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-row items-stretch">
        <div
          className={`flex min-h-0 flex-1 flex-col overflow-hidden bg-bg-page-body py-[20px] pr-[12px] ${
            selectedKycData && !isPanelCollapsed ? "w-3/4" : "w-full"
          }`}
        >
          {/* Filter Row */}
          <div className="mb-4 flex shrink-0 flex-row items-center justify-between">
            <div className="flex justify-start space-x-3 items-center">
              <p className="text-text-base text-[10px] font-bold">
                Filter by KYC Status:
              </p>
              <div className="flex space-x-3">
                {[
                  "incomplete",
                  "pending_review",
                  "approved",
                  "rejected",
                  "retry",
                ].map((status) => (
                  <div
                    key={status}
                    onClick={
                      loading ? undefined : () => handleKycStatusChange(status)
                    }
                    className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                      loading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      filterKycStatus === status
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    {status
                      .replace("_", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </div>
                ))}
              </div>
            </div>
            <div className="w-[360px] flex-shrink-0">
              <GlobalSearchInputField
                value={searchTerm}
                onChange={handleSearchChange}
                onSearch={handleSearch}
                onClear={handleClearSearch}
                disabled={loading}
                isSearchApplied={Boolean(appliedSearchTerm)}
                placeholder="Search by User ID / Name / Email"
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            {loading && kycData.length === 0 ? (
              <AgGridSkeleton
                columnDefs={columnDefs}
                style={{ height: "100%", width: "100%", minHeight: 0 }}
              />
            ) : (
              <AgGridReactTable
                ref={gridRef}
                rowData={kycData}
                columnDefs={columnDefs}
                onRowClicked={onRowClicked}
                onBodyScroll={onBodyScroll}
                getRowId={(params) =>
                  params.data?.userId != null
                    ? params.data.userId.toString()
                    : String(params.data?.id ?? params.node?.id ?? "")
                }
                error={error}
                style={{ height: "100%", width: "100%", minHeight: 0 }}
              />
            )}
          </div>
        </div>

        {selectedKycData && (
          <div
            className={`min-h-0 flex-shrink-0 self-stretch bg-bg-page-body py-4 border-l border-border-subtle transition-all duration-300 relative ${
              isPanelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
            }`}
          >
            <button
              onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
              className="absolute top-1/2 -translate-y-1/2 z-20 bg-bg-button-secondary text-text-base rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
              style={{
                left: isPanelCollapsed ? "calc(100% - 32px)" : "-16px",
                width: "32px",
                height: "60px",
              }}
              aria-label={isPanelCollapsed ? "Expand panel" : "Collapse panel"}
            >
              <span className="material-symbols-outlined text-[20px] group-hover:scale-110 transition-transform">
                {isPanelCollapsed ? "chevron_left" : "chevron_right"}
              </span>
            </button>

            <div
              className={`overflow-auto h-full transition-opacity duration-300 ${
                isPanelCollapsed
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100"
              }`}
            >
              <KycApprovalDetails
                data={selectedKycData}
                onRefresh={handleRefresh}
              />
            </div>
          </div>
        )}
      </div>

      <SettledAccountReponseSlider
        isOpen={showDumpModal}
        onClose={handleCloseDumpModal}
        data={modalData}
        isSuccess={true}
        title={modalTitle}
      />
    </div>
  );
};

export default KycApprovalList;
